<template>
  <el-dialog
    :title="$t('QuotationCenter.components.excelImportMethod')"
    :visible.sync="dialogVisible"
    width="520px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    append-to-body
    @closed="resetImportDialog"
  >
    <el-form label-position="top" class="import-dialog-form">
      <el-form-item :label="$t('QuotationCenter.components.clickUpload')">
        <el-upload
          ref="importUpload"
          class="import-upload"
          drag
          action=""
          accept=".xls,.xlsx"
          :limit="1"
          :file-list="importFileList"
          :auto-upload="false"
          :on-change="handleImportFileChange"
          :on-exceed="handleImportFileExceed"
          :on-remove="handleImportFileRemove"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">{{ $t('QuotationCenter.components.clickUpload') }}</div>
          <div slot="tip" class="el-upload__tip">{{ $t('QuotationCenter.components.onlySupportExcel') }}</div>
        </el-upload>
      </el-form-item>
      <el-form-item :label="$t('fileSearch.table.FileType')">
        <el-radio-group v-model="importForm.type">
          <el-radio :label="1">Supports Incremental, Direct Mu</el-radio>
          <el-radio :label="2">FirstAdditional Weight</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="dialogVisible = false">{{ $t('cancelText') }}</el-button>
      <el-button size="small" type="primary" :loading="loading" @click="confirmImport">{{ $t('toast.okText') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import * as XLSX from 'xlsx';
import {
  detectImportTypeFromPricingMethod,
  isDetectedImportTypeSupported,
  isSelectedImportTypeMatched,
} from './quotation-import-header';

const IMPORT_ACCEPT_TYPES = ['.xls', '.xlsx'];

export default {
  name: 'QuotationImportDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    defaultType: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      importFile: null,
      importFileList: [],
      importForm: {
        type: this.defaultType,
      },
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
  },
  methods: {
    resetImportDialog() {
      this.importFile = null;
      this.importFileList = [];
      this.importForm = {
        type: this.defaultType,
      };
      this.$nextTick(() => {
        this.$refs.importUpload && this.$refs.importUpload.clearFiles();
      });
      this.$emit('closed');
    },
    setImportFile(rawFile, uploadFile) {
      if (!rawFile) {
        return false;
      }
      const fileName = rawFile.name || '';
      const suffixIndex = fileName.lastIndexOf('.');
      const suffix = suffixIndex > -1 ? fileName.slice(suffixIndex).toLowerCase() : '';
      if (!IMPORT_ACCEPT_TYPES.includes(suffix)) {
        this.$message.warning(this.$t('QuotationCenter.components.onlySupportExcel'));
        this.clearImportFile();
        return false;
      }
      this.importFile = rawFile;
      this.importFileList = [uploadFile || {
        name: rawFile.name,
        uid: rawFile.uid || Date.now(),
        status: 'ready',
        raw: rawFile,
      }];
      return true;
    },
    clearImportFile() {
      this.$refs.importUpload && this.$refs.importUpload.clearFiles();
      this.importFile = null;
      this.importFileList = [];
    },
    async handleImportFileChange(file) {
      if (!this.setImportFile(file && file.raw, file)) {
        return;
      }
      const valid = await this.validateImportFileSupported();
      if (!valid) {
        this.clearImportFile();
      }
    },
    async handleImportFileExceed(files) {
      const rawFile = files && files[0];
      this.$refs.importUpload && this.$refs.importUpload.clearFiles();
      if (!this.setImportFile(rawFile)) {
        return;
      }
      const valid = await this.validateImportFileSupported();
      if (!valid) {
        this.clearImportFile();
      }
    },
    handleImportFileRemove() {
      this.importFile = null;
      this.importFileList = [];
    },
    readImportWorkbookFirstSheetRows(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const workbook = XLSX.read(event.target.result, { type: 'array' });
            const firstSheetName = (workbook.SheetNames || [])[0];
            const worksheet = firstSheetName ? workbook.Sheets[firstSheetName] : null;
            const rows = worksheet ? XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: false,
              blankrows: false,
              defval: '',
            }) : [];
            resolve(rows);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
      });
    },
    async detectImportFileType() {
      const firstSheetRows = await this.readImportWorkbookFirstSheetRows(this.importFile);
      return detectImportTypeFromPricingMethod(firstSheetRows);
    },
    async validateImportFileSupported() {
      try {
        const detectedType = await this.detectImportFileType();
        if (!isDetectedImportTypeSupported(detectedType)) {
          this.$message.error(this.$t('formatErrorText'));
          return false;
        }
        return true;
      } catch (error) {
        this.$message.error(this.$t('formatErrorText'));
        return false;
      }
    },
    async validateImportFileHeader() {
      try {
        const detectedType = await this.detectImportFileType();
        if (!isSelectedImportTypeMatched(detectedType, this.importForm.type)) {
          this.$message.error(this.$t('formatErrorText'));
          return false;
        }
        return true;
      } catch (error) {
        this.$message.error(this.$t('formatErrorText'));
        return false;
      }
    },
    async confirmImport() {
      if (!this.importFile) {
        this.$message.warning(this.$t('ExcelConfig.upload'));
        return;
      }
      const validHeader = await this.validateImportFileHeader();
      if (!validHeader) {
        return;
      }
      this.$emit('confirm', {
        file: this.importFile,
        type: this.importForm.type,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.import-upload {
  width: 100%;
}

::v-deep .import-upload .el-upload,
::v-deep .import-upload .el-upload-dragger {
  width: 100%;
}

::v-deep .import-dialog-form .el-form-item {
  margin-bottom: 18px;
}

::v-deep .import-dialog-form .el-radio {
  margin-bottom: 8px;
  line-height: 20px;
}
</style>
